import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';

interface Lead {
    _id: string;
    name: string;
    mobile: string;
    email: string;
    status: string;
    source: string;
    city: string;
    budget: string;
    propertyType: string;
    timeline: { status: string; notes: string; date: string }[];
    siteVisit?: {
        date: string;
        time: string;
        agentName: string;
        project?: { _id: string; projectName: string; slug?: string };
        notes: string;
    };
    createdAt: string;
}

export default function AdminLeads() {
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [viewLead, setViewLead] = useState<Lead | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newLead, setNewLead] = useState({
        name: '', mobile: '', email: '', city: '', budget: '', propertyType: '', source: 'Online'
    });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

    useEffect(() => {
        fetchLeads();
    }, []);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/leads`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setLeads(Array.isArray(data) ? data : []);
            } else {
                setLeads([]);
                toast.error(data.message || 'Failed to load leads');
            }
        } catch (err) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLead = async () => {
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/leads`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newLead)
            });
            if (res.ok) {
                toast.success('Lead created successfully');
                setShowCreateModal(false);
                fetchLeads();
            }
        } catch (err) {
            toast.error('Error creating lead');
        }
    };

    const handleViewDetail = async (id: string) => {
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/leads/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setViewLead(data);
            }
        } catch (err) {
            toast.error('Failed to load lead details');
        }
    };

    const handleUpdateStatus = async (id: string, status: string) => {
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/leads/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status })
            });
            if (res.ok) {
                toast.success('Status updated');
                setLeads(prev => prev.map(l => l._id === id ? { ...l, status } : l));
            }
        } catch (err) {
            toast.error('Error updating status');
        }
    };

    const handleExpungeLead = async (id: string) => {
        try {
            const token = localStorage.getItem('resido_token');
            const res = await fetch(`${API_BASE}/leads/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                toast.success('Lead expunged successfully');
                setLeads(prev => prev.filter(l => l._id !== id));
                setViewLead(null);
                setShowDeleteConfirm(null);
            }
        } catch (err) {
            toast.error('Error expunging lead');
        }
    };

    const filtered = leads.filter(l => {
        const matchSearch = l.name.includes(search) || 
                           l.mobile.includes(search) || 
                           (l.email && l.email.includes(search));
        const matchStatus = statusFilter === 'All' || l.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const counts = {
        all: leads.length,
        new: leads.filter(l => l.status === 'New Inquiry').length,
        negotiation: leads.filter(l => l.status === 'Negotiation').length,
        booked: leads.filter(l => l.status === 'Booked').length,
    };

    return (
        <>
            <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl font-bold text-[#05144b] mb-2 leading-none">CRM Leads Hub</h1>
                    <p className="text-[#05144b] font-semibold text-[14px]">Synchronized multi-channel interest stream and interaction log.</p>
                </div>
                <button 
                    onClick={() => setShowCreateModal(true)}
                    className="px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md text-[12px] md:text-[14px] font-bold transition-all active:scale-95 flex items-center justify-center gap-3 shadow-md"
                >
                    <i className="fa-solid fa-plus text-xs"></i> Initialize lead
                </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                    { label: 'Total inflow', count: counts.all, icon: 'fa-users', color: 'bg-blue-950', filter: 'All' },
                    { label: 'Fresh inquiries', count: counts.new, icon: 'fa-star', color: 'bg-blue-950', filter: 'New Inquiry' },
                    { label: 'Active pipeline', count: counts.negotiation, icon: 'fa-bolt', color: 'bg-blue-950', filter: 'Negotiation' },
                    { label: 'Success ratio', count: counts.booked, icon: 'fa-trophy', color: 'bg-blue-950', filter: 'Booked' },
                ].map((s, i) => (
                    <button 
                        key={i}
                        onClick={() => setStatusFilter(s.filter)}
                        className={`relative overflow-hidden group p-8 rounded-md border transition-all text-left ${
                            statusFilter === s.filter 
                            ? 'bg-white dark:bg-slate-900 border-[#05144b] ring-2 ring-[#05144b]/10 shadow-xl' 
                            : 'bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-800 hover:border-[#05144b]/30'
                        }`}
                    >
                        <div className={`w-12 h-12 rounded-md bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-xl`}>
                            <i className={`fa-solid ${s.icon} text-lg`}></i>
                        </div>
                        <p className="text-[13px] font-bold text-[#05144b] mb-1">{s.label}</p>
                        <h4 className="text-3xl font-bold text-[#05144b] leading-none tabular-nums">{s.count}</h4>
                    </button>
                ))}
            </div>
                {/* Main Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center shadow-xl">
                            <i className="fa-solid fa-id-card-clip text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-xl md:text-2xl font-bold text-[#05144b] leading-none">Master Database</h3>
                            <p className="text-[13px] md:text-[14px] font-bold text-[#05144b] mt-1.5">{filtered.length} Leads indexed</p>
                        </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                        <div className="relative group w-full md:w-[220px]">
                            <select
                                className="w-full bg-white dark:bg-slate-800 border border-gray-100 rounded-md py-4 pl-6 pr-12 text-[13px] font-semibold text-[#05144b] focus:ring-2 focus:ring-[#05144b] shadow-inner appearance-none cursor-pointer transition-all"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="New Inquiry">New Inquiry</option>
                                <option value="Requirement">Requirement</option>
                                <option value="Property Suggested">Property Suggested</option>
                                <option value="Visit Scheduled">Visit Scheduled</option>
                                <option value="Negotiation">Negotiation</option>
                                <option value="Booked">Booked</option>
                                <option value="Lost">Lost</option>
                            </select>
                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                <i className="fa-solid fa-chevron-down text-[8px] text-black"></i>
                            </div>
                        </div>

                        <div className="relative group w-full md:w-[350px]">
                            <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-black/20 group-focus-within:text-black transition-colors text-xs"></i>
                            <input 
                                type="text" 
                                className="w-full bg-white dark:bg-slate-800 border border-gray-100 rounded-md py-4 pl-14 pr-6 text-[15px] font-semibold text-[#05144b] focus:ring-2 focus:ring-[#05144b] shadow-inner placeholder:text-gray-400 transition-all"
                                placeholder="Find lead by name, phone..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="w-16 h-16 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            <p className="text-[#05144b] font-bold text-[14px]">Initializing Lead Ledger...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-32 text-center">
                            <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-8 border border-gray-100">
                                <i className="fa-solid fa-user-slash text-4xl text-[#05144b]/10"></i>
                            </div>
                            <h3 className="text-xl font-bold text-[#05144b] mb-2">No Leads Detected</h3>
                            <p className="text-[#05144b]/40 text-sm max-w-xs mx-auto font-bold">Either your search criteria is too specific or you haven't captured any leads yet.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-gray-100">
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Customer Profile</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Budget & Specs</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Origin Pool</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Engagement</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Status Protocol</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100 text-right">Ops</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filtered.map(l => (
                                    <tr key={l._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all text-xs">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-2xl bg-[#05144b] p-0.5 shadow-lg">
                                                    <div className="w-full h-full rounded-[14px] bg-white dark:bg-slate-900 flex items-center justify-center">
                                                        <span className="text-[#05144b] font-bold text-lg">{l.name.charAt(0).toUpperCase()}</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-[#05144b] text-[15px]">{l.name}</h4>
                                                    <div className="flex items-center gap-3 mt-1 text-[12px] font-medium text-[#05144b]/60">
                                                        <span className="flex items-center gap-1"><i className="fa-solid fa-phone text-[10px]"></i> {l.mobile}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div>
                                                <h4 className="font-bold text-[#05144b] text-[15px] line-clamp-1">{l.budget || 'Unspecified'}</h4>
                                                <div className="flex items-center gap-1.5 mt-1">
                                                    <i className="fa-solid fa-building text-[10px] text-[#05144b]/20"></i>
                                                    <span className="text-[12px] font-medium text-[#05144b]/60 line-clamp-1">{l.propertyType || 'Generic'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div>
                                                <div className="text-[12px] font-bold text-[#05144b]">{l.source}</div>
                                                <div className="text-[12px] font-medium text-[#05144b]/60 mt-0.5">{l.city || 'Anywhere'}</div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-2">
                                                <a 
                                                    href={`tel:${l.mobile}`}
                                                    className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    title="Call Lead"
                                                >
                                                    <i className="fa-solid fa-phone text-xs"></i>
                                                </a>
                                                <a 
                                                    href={`https://wa.me/91${l.mobile.replace(/\D/g, '')}?text=Hi ${l.name}, I'm calling from My Property Guru regarding your real estate inquiry.`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    title="WhatsApp Lead"
                                                >
                                                    <i className="fa-brands fa-whatsapp text-lg"></i>
                                                </a>
                                                {l.siteVisit?.project?._id && (
                                                    <a 
                                                        href={`/project-details/${l.siteVisit.project.slug || l.siteVisit.project._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-[#05144b] hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                        title="View Associated Project"
                                                    >
                                                        <i className="fa-solid fa-eye text-xs"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="relative group/sel">
                                                <select
                                                    className={`appearance-none font-bold text-[12px] px-5 py-2.5 rounded-full border-none focus:ring-2 focus:ring-[#05144b] transition-all cursor-pointer ${
                                                        l.status === 'Booked' ? 'bg-emerald-100 text-emerald-700' :
                                                        l.status === 'Lost' ? 'bg-rose-100 text-rose-700' : 
                                                        'bg-amber-100 text-amber-700'
                                                    }`}
                                                    value={l.status}
                                                    onChange={e => handleUpdateStatus(l._id, e.target.value)}
                                                >
                                                    <option value="New Inquiry">New Inquiry</option>
                                                    <option value="Requirement">Requirement</option>
                                                    <option value="Property Suggested">Property Suggested</option>
                                                    <option value="Visit Scheduled">Visit Scheduled</option>
                                                    <option value="Negotiation">Negotiation</option>
                                                    <option value="Booked">Booked</option>
                                                    <option value="Lost">Lost</option>
                                                </select>
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                                    <i className="fa-solid fa-chevron-down text-[8px]"></i>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    className="w-10 h-10 rounded-xl bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 transition-all flex items-center justify-center shadow-sm"
                                                    onClick={() => handleViewDetail(l._id)}
                                                    title="View Pipeline"
                                                >
                                                    <i className="fa-solid fa-diagram-next text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-slate-800 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    onClick={() => setShowDeleteConfirm(l._id)}
                                                    title="Expunge Lead"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Premium Details Overlay */}
            {viewLead && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setViewLead(null)}></div>
                    <div className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-[40px] border border-blue-900/10 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden max-h-[90vh] flex flex-col">
                        <div className="relative h-48 bg-gradient-to-r from-[#05144b] to-blue-900 flex flex-col justify-end p-10 shrink-0">
                                <button 
                                onClick={() => setViewLead(null)}
                                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                            <div className="flex items-center gap-8">
                                <div className="w-28 h-28 rounded-3xl bg-white dark:bg-slate-900 p-1 shadow-2xl">
                                    <div className="w-full h-full rounded-[20px] bg-white dark:bg-slate-800 flex items-center justify-center">
                                        <span className="text-5xl font-bold text-[#05144b]">
                                            {viewLead.name.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h2 className="text-3xl font-bold text-white tracking-tight">{viewLead?.name}</h2>
                                    <div className="flex items-center gap-3 mt-3">
                                        <div className={`px-4 py-1.5 rounded-full text-[10px] font-bold ${
                                            viewLead?.status === 'Booked' ? 'bg-emerald-400 text-emerald-900' :
                                            viewLead?.status === 'Lost' ? 'bg-rose-400 text-rose-900' : 
                                            'bg-amber-400 text-amber-900'
                                        }`}>
                                            {viewLead?.status}
                                        </div>
                                        <div className="px-4 py-1.5 rounded-full bg-white/20 text-white text-[10px] font-bold backdrop-blur-md">
                                            {viewLead?.source} Origin
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 overflow-y-auto custom-scrollbar flex-grow">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                                <div className="space-y-10">
                                    <div>
                                        <p className="text-[10px] font-bold text-[#05144b] mb-4">Core Metadata</p>
                                        <div className="space-y-4">
                                            <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-800">
                                                <p className="text-[8px] font-bold text-[#05144b] mb-1">Mobile Access</p>
                                                <p className="text-sm font-bold text-slate-900 dark:text-white">{viewLead?.mobile}</p>
                                            </div>
                                            <div className="p-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-800">
                                                <p className="text-[8px] font-bold text-[#05144b] mb-1">Electronic Mail</p>
                                                <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{viewLead?.email || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-[10px] font-bold text-[#05144b] mb-4">Requirement Profile</p>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 rounded-2xl bg-gray-50 dark:bg-blue-900/10 border border-gray-100 dark:border-blue-900/20">
                                                <p className="text-[8px] font-bold text-[#05144b] mb-1">Budget</p>
                                                <p className="text-xs font-bold text-[#05144b] dark:text-white">{viewLead?.budget || 'N/A'}</p>
                                            </div>
                                            <div className="p-4 rounded-2xl bg-gray-50 dark:bg-indigo-900/10 border border-gray-100 dark:border-indigo-900/20">
                                                <p className="text-[8px] font-bold text-[#05144b] mb-1">Type</p>
                                                <p className="text-xs font-bold text-[#05144b] dark:text-white">{viewLead?.propertyType || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="md:col-span-2 space-y-10">
                                    {viewLead?.siteVisit && (
                                        <div>
                                            <p className="text-[10px] font-bold text-rose-500 mb-6 flex items-center gap-2">
                                                <i className="fa-solid fa-calendar-check"></i> Site Visit Activation
                                            </p>
                                            <div className="p-8 rounded-[32px] bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/20">
                                                <div className="flex items-center gap-4 mb-6">
                                                    <div className="w-12 h-12 rounded-2xl bg-rose-500 flex items-center justify-center text-white shadow-lg shadow-rose-500/20">
                                                        <i className="fa-solid fa-location-dot"></i>
                                                    </div>
                                                    <div>
                                                        <h4 className="text-lg font-bold text-slate-900 dark:text-white">
                                                            {(viewLead?.siteVisit as any)?.project?.projectName || 'Project Site Visit'}
                                                        </h4>
                                                        <p className="text-[10px] font-bold text-rose-600">
                                                            Scheduled for {viewLead?.siteVisit?.date && new Date(viewLead.siteVisit.date).toLocaleDateString()} at {viewLead?.siteVisit?.time}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-6 mb-6">
                                                    <div className="bg-white/50 dark:bg-slate-900/50 p-4 rounded-2xl border border-rose-100/50 dark:border-rose-900/10">
                                                        <p className="text-[8px] font-bold text-[#05144b] mb-1">Escorting Agent</p>
                                                        <p className="text-xs font-bold text-slate-900 dark:text-white">{viewLead?.siteVisit?.agentName || 'TBD'}</p>
                                                    </div>
                                                    <div className="bg-white/50 dark:bg-slate-900/50 p-4 rounded-2xl border border-rose-100/50 dark:border-rose-900/10">
                                                        <p className="text-[8px] font-bold text-[#05144b] mb-1">Status</p>
                                                        <p className="text-xs font-bold text-rose-600">Confirmed</p>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-[#05144b] dark:text-[#05144b] leading-relaxed bg-white/30 dark:bg-black/20 p-4 rounded-xl">
                                                    "{viewLead?.siteVisit?.notes || 'No specific visit notes recorded.'}"
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <p className="text-[10px] font-bold text-[#05144b] mb-6 flex items-center gap-2">
                                            <i className="fa-solid fa-timeline text-[#05144b]"></i> Lead Timeline / Progression
                                        </p>
                                        <div className="space-y-6 relative before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100 dark:before:bg-slate-800">
                                            {(viewLead?.timeline || []).slice().reverse().map((t: any, i: number) => (
                                                <div key={i} className="flex gap-6 relative">
                                                    <div className="w-6 h-6 rounded-full bg-white dark:bg-slate-900 border-4 border-[#05144b] shrink-0 z-10 shadow-sm"></div>
                                                    <div className="p-5 rounded-2xl bg-white dark:bg-slate-800/80 border border-gray-100 dark:border-slate-800 flex-grow shadow-sm hover:shadow-md transition-shadow">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <h5 className="text-[11px] font-bold text-[#05144b] dark:text-white">{t.status}</h5>
                                                            <span className="text-[8px] font-bold text-[#05144b]/40">{t.date && new Date(t.date).toLocaleDateString()}</span>
                                                        </div>
                                                        <p className="text-xs text-[#05144b]/60 dark:text-[#05144b] leading-relaxed">"{t.notes}"</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {(!viewLead.timeline || viewLead.timeline.length === 0) && (
                                                <div className="p-8 text-center bg-white dark:bg-slate-800/50 rounded-3xl border border-dashed border-blue-900/10 dark:border-slate-700">
                                                    <p className="text-xs font-bold text-blue-950 uppercase tracking-widest">No historical data recorded</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-8 bg-blue-50/50 dark:bg-slate-800/50 rounded-[32px] border border-blue-100 dark:border-blue-900/20 flex flex-wrap items-center justify-between gap-6">
                                        <div>
                                            <h5 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight">Active Engagement</h5>
                                            <p className="text-[10px] font-bold text-blue-950 uppercase tracking-widest mt-1">Direct communication with lead</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <a 
                                                href={`tel:${viewLead.mobile}`}
                                                className="px-6 py-3 rounded-2xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-black uppercase tracking-widest text-[10px] border border-blue-900/10 dark:border-slate-800 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all flex items-center gap-2 shadow-sm"
                                            >
                                                <i className="fa-solid fa-phone"></i> Voice Call
                                            </a>
                                            <a 
                                                href={`https://wa.me/91${viewLead.mobile.replace(/\D/g, '')}?text=Hi ${viewLead.name}, I'm calling from My Property Guru regarding your inquiry.`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-6 py-3 rounded-2xl bg-green-600 text-white font-black uppercase tracking-widest text-[10px] hover:bg-green-700 transition-all flex items-center gap-2 shadow-lg shadow-green-500/20"
                                            >
                                                <i className="fa-brands fa-whatsapp text-sm"></i> WhatsApp
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 bg-white dark:bg-slate-800/50 border-t border-blue-900/10 dark:border-slate-800 shrink-0 flex justify-end">
                            <button 
                                className="px-10 py-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-black uppercase tracking-widest text-[10px] hover:bg-rose-600 hover:text-white transition-all mr-auto"
                                onClick={() => setShowDeleteConfirm(viewLead._id)}
                            >
                                Expunge Entry
                            </button>
                            <button 
                                className="px-10 py-4 rounded-2xl bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 font-black uppercase tracking-[0.2em] text-[10px] hover:scale-105 transition-transform active:scale-95 shadow-xl shadow-blue-900/10 dark:shadow-none"
                                onClick={() => setViewLead(null)}
                            >
                                Dismiss Lead
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Create Lead Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setShowCreateModal(false)}></div>
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[40px] border border-blue-900/10 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden">
                        <div className="p-10 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center shadow-lg shadow-blue-500/20">
                                    <i className="fa-solid fa-plus-circle"></i>
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white uppercase tracking-tight">Manual Ingestion</h3>
                            </div>
                            <button onClick={() => setShowCreateModal(false)} className="text-blue-950 hover:text-slate-900 dark:hover:text-white transition-colors">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Customer Name</label>
                                <input 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                    type="text" 
                                    placeholder="e.g. Rahul Sharma"
                                    onChange={e => setNewLead({ ...newLead, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Mobile Access</label>
                                <input 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                    type="text" 
                                    placeholder="+91..."
                                    onChange={e => setNewLead({ ...newLead, mobile: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Email (Optional)</label>
                                <input 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                    type="email" 
                                    placeholder="rahul@example.com"
                                    onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Source Stream</label>
                                <select 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner appearance-none cursor-pointer"
                                    onChange={e => setNewLead({ ...newLead, source: e.target.value })}
                                >
                                    <option value="Online">Online / Web</option>
                                    <option value="Call">Direct Call</option>
                                    <option value="Visit">Site Visit</option>
                                    <option value="Agent">External Agent</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Financial Budget</label>
                                <input 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                    type="text" 
                                    placeholder="e.g. 1.5 Cr"
                                    onChange={e => setNewLead({ ...newLead, budget: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-blue-950 uppercase tracking-widest ml-1">Asset Preference</label>
                                <input 
                                    className="w-full bg-white dark:bg-slate-800 border-none rounded-2xl px-6 py-4 text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-[#05144b] shadow-inner"
                                    type="text" 
                                    placeholder="e.g. 3BHK Penthouse"
                                    onChange={e => setNewLead({ ...newLead, propertyType: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="p-10 bg-white dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-800 flex justify-end gap-4">
                            <button 
                                className="px-8 py-4 rounded-2xl bg-white dark:bg-slate-900 text-black font-bold uppercase tracking-widest text-[10px] border border-gray-100 dark:border-slate-800 hover:bg-gray-50 transition-all"
                                onClick={() => setShowCreateModal(false)}
                            >
                                Abort
                            </button>
                            <button 
                                className="px-10 py-4 rounded-2xl bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 font-bold uppercase tracking-widest text-[10px] hover:scale-105 transition-transform active:scale-95 shadow-xl shadow-blue-900/10"
                                onClick={handleCreateLead}
                            >
                                Commit Lead
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </AdminLayout>

            {/* Custom Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 rounded-full flex items-center justify-center mx-auto mb-6 text-rose-600">
                                <i className="fa-solid fa-triangle-exclamation text-3xl"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-black dark:text-white mb-2">Confirm Expunge</h3>
                            <p className="text-black/40 dark:text-blue-950 font-bold text-sm mb-8 leading-relaxed">
                                Are you absolutely sure you want to expunge this lead? This action is <span className="text-rose-600 font-bold uppercase tracking-widest">Irreversible</span> and will remove all history.
                            </p>
                            
                            <div className="flex flex-col gap-3">
                                <button 
                                    onClick={() => showDeleteConfirm && handleExpungeLead(showDeleteConfirm)}
                                    className="w-full py-4 bg-rose-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-rose-700 transition-all shadow-lg shadow-rose-200"
                                >
                                    Yes, Expunge Lead
                                </button>
                                <button 
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="w-full py-4 bg-slate-50 dark:bg-slate-800 text-blue-950 dark:text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}


